/**
 * Sistema di dialoghi personalizzati per sostituire alert(), confirm(), prompt()
 * Fornisce UI moderne e integrate invece dei popup nativi del browser
 */

class CustomDialogs {
  constructor() {
    this.activeDialogs = new Set();
    /** @type {AbortController|null} */
    this._dialogAbort = null;
    this.ensureOverlayExists();
  }

  /** Annulla listener del dialogo precedente (evita accumulo overlay/keydown). */
  _beginDialogSession() {
    if (this._dialogAbort) {
      this._dialogAbort.abort();
      this.hideOverlay();
    }
    this._dialogAbort = new AbortController();
    return this._dialogAbort.signal;
  }

  _endDialogSession() {
    this._dialogAbort?.abort();
    this._dialogAbort = null;
    this.hideOverlay();
  }

  /**
   * Crea overlay di sfondo per i modali se non esiste
   */
  ensureOverlayExists() {
    if (!document.getElementById('custom-dialog-overlay')) {
      const overlay = document.createElement('div');
      overlay.id = 'custom-dialog-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(4px);
        z-index: 10000;
        display: none;
        opacity: 0;
        transition: opacity 0.2s ease;
      `;
      document.body.appendChild(overlay);
    }
  }

  /**
   * Mostra overlay con animazione
   */
  showOverlay() {
    const overlay = document.getElementById('custom-dialog-overlay');
    overlay.style.display = 'flex';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.pointerEvents = 'auto';
    setTimeout(() => overlay.style.opacity = '1', 10);
  }

  /**
   * Nasconde overlay con animazione
   */
  hideOverlay() {
    const overlay = document.getElementById('custom-dialog-overlay');
    if (!overlay) return;
    overlay.style.opacity = '0';
    overlay.style.pointerEvents = 'none';
    setTimeout(() => {
      overlay.style.display = 'none';
      overlay.innerHTML = '';
    }, 200);
  }

  _dialogShellStyles() {
    return `
      background: white;
      border-radius: 16px;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
      max-width: 480px;
      width: 90%;
      padding: 0;
      transform: scale(0.9) translateY(20px);
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      overflow: hidden;
    `;
  }

  _beginModalPromise() {
    const signal = this._beginDialogSession();
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      this._endDialogSession();
      return value;
    };
    return { signal, finish };
  }

  _mountDialog(dialog) {
    const overlay = document.getElementById('custom-dialog-overlay');
    overlay.appendChild(dialog);
    this.showOverlay();
    setTimeout(() => {
      dialog.style.transform = 'scale(1) translateY(0)';
    }, 10);
    return overlay;
  }

  _wireDialogButtonHover(cancelBtn, confirmBtn, signal, confirmStyle = {}) {
    const { background = '#3b82f6', hover = '#2563eb' } = confirmStyle;
    cancelBtn.addEventListener('mouseenter', () => {
      cancelBtn.style.background = '#f9fafb';
    }, { signal });
    cancelBtn.addEventListener('mouseleave', () => {
      cancelBtn.style.background = 'white';
    }, { signal });
    confirmBtn.addEventListener('mouseenter', () => {
      confirmBtn.style.background = hover;
    }, { signal });
    confirmBtn.addEventListener('mouseleave', () => {
      confirmBtn.style.background = background;
    }, { signal });
  }

  _wireDialogDismiss({
    overlay,
    signal,
    onCancel,
    onConfirm,
    cancelBtn,
    confirmBtn,
    keyTarget = document,
  }) {
    cancelBtn.addEventListener('click', onCancel, { signal });
    confirmBtn.addEventListener('click', onConfirm, { signal });
    keyTarget.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onConfirm();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }, { signal });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) onCancel();
    }, { signal });
  }

  /**
   * Toast notification personalizzato (migliora quello esistente)
   * @param {string} message - Messaggio da mostrare
   * @param {string} type - Tipo: 'success', 'error', 'warning', 'info'
   * @param {number} duration - Durata in ms (default: 4000)
   */
  showToast(message, type = 'info', duration = 4000) {
    const toast = document.createElement('div');
    toast.className = `custom-toast toast-${type}`;
    
    const colors = {
      success: '#10b981',
      error: '#ef4444', 
      warning: '#f59e0b',
      info: '#3b82f6'
    };
    
    const icons = {
      success: '✅',
      error: '❌',
      warning: '⚠️', 
      info: 'ℹ️'
    };
    
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${colors[type]};
      color: white;
      padding: 16px 20px;
      border-radius: 12px;
      box-shadow: 0 10px 25px rgba(0,0,0,0.2);
      z-index: 10001;
      font-size: 14px;
      font-weight: 500;
      transform: translateX(100%);
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      max-width: 350px;
      display: flex;
      align-items: center;
      gap: 8px;
    `;
    
    toast.innerHTML = `
      <span style="font-size: 16px;">${icons[type]}</span>
      <span>${message}</span>
    `;
    
    document.body.appendChild(toast);
    
    // Anima entrata
    setTimeout(() => toast.style.transform = 'translateX(0)', 10);
    
    // Rimuovi dopo durata specificata
    setTimeout(() => {
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, duration);
    
    return toast;
  }

  /**
   * Modal di input personalizzato (sostituisce prompt())
   * @param {string} title - Titolo del modal
   * @param {string} message - Messaggio/descrizione
   * @param {string} defaultValue - Valore di default
   * @param {string} placeholder - Placeholder per input
   * @returns {Promise<string|null>} - Valore inserito o null se annullato
   */
  async showInput(title, message, defaultValue = '', placeholder = '') {
    return new Promise((resolve) => {
      const { signal, finish: settle } = this._beginModalPromise();
      const finish = (value) => resolve(settle(value));

      const dialog = document.createElement('div');
      dialog.className = 'custom-input-dialog';
      dialog.style.cssText = this._dialogShellStyles();

      dialog.innerHTML = `
        <div style="padding: 24px 24px 16px 24px; border-bottom: 1px solid #e5e7eb;">
          <h3 style="
            margin: 0 0 8px 0;
            font-size: 18px;
            font-weight: 600;
            color: #111827;
          ">${title}</h3>
          <p style="
            margin: 0;
            font-size: 14px;
            color: #6b7280;
            line-height: 1.5;
          ">${message}</p>
        </div>
        
        <div style="padding: 20px 24px;">
          <input 
            type="text" 
            class="dialog-input"
            value="${defaultValue}" 
            placeholder="${placeholder}"
            style="
              width: 100%;
              border: 2px solid #e5e7eb;
              border-radius: 8px;
              padding: 12px 16px;
              font-size: 14px;
              font-family: inherit;
              transition: border-color 0.2s ease;
              outline: none;
              box-sizing: border-box;
            "
          />
        </div>
        
        <div style="
          display: flex;
          gap: 12px;
          padding: 16px 24px 24px 24px;
          justify-content: flex-end;
        ">
          <button class="dialog-cancel" style="
            padding: 10px 20px;
            border: 1px solid #d1d5db;
            background: white;
            color: #374151;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
          ">Annulla</button>
          <button class="dialog-confirm" style="
            padding: 10px 20px;
            border: none;
            background: #3b82f6;
            color: white;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
          ">OK</button>
        </div>
      `;

      const overlay = this._mountDialog(dialog);

      const input = dialog.querySelector('.dialog-input');
      const cancelBtn = dialog.querySelector('.dialog-cancel');
      const confirmBtn = dialog.querySelector('.dialog-confirm');

      setTimeout(() => {
        input.focus();
        if (defaultValue) input.select();
      }, 100);

      this._wireDialogButtonHover(cancelBtn, confirmBtn, signal);

      input.addEventListener('focus', () => {
        input.style.borderColor = '#3b82f6';
      }, { signal });
      input.addEventListener('blur', () => {
        input.style.borderColor = '#e5e7eb';
      }, { signal });

      const handleCancel = () => finish(null);
      const handleConfirm = () => finish(input.value.trim());

      this._wireDialogDismiss({
        overlay,
        signal,
        onCancel: handleCancel,
        onConfirm: handleConfirm,
        cancelBtn,
        confirmBtn,
        keyTarget: input,
      });
    });
  }

  /**
   * Modal di conferma personalizzato (sostituisce confirm())
   * @param {string} title - Titolo del modal
   * @param {string} message - Messaggio/descrizione  
   * @param {string} confirmText - Testo pulsante conferma (default: "Conferma")
   * @param {string} cancelText - Testo pulsante annulla (default: "Annulla")
   * @param {string} type - Tipo: 'danger', 'warning', 'info' (default: 'info')
   * @returns {Promise<boolean>} - true se confermato, false se annullato
   */
  async showConfirm(title, message, confirmText = 'Conferma', cancelText = 'Annulla', type = 'info') {
    return new Promise((resolve) => {
      const { signal, finish: settle } = this._beginModalPromise();
      const finish = (value) => resolve(settle(value));

      const colors = {
        danger: { bg: '#fef2f2', border: '#fecaca', btn: '#ef4444', btnHover: '#dc2626' },
        warning: { bg: '#fffbeb', border: '#fed7aa', btn: '#f59e0b', btnHover: '#d97706' },
        info: { bg: '#eff6ff', border: '#bfdbfe', btn: '#3b82f6', btnHover: '#2563eb' }
      };

      const icons = {
        danger: '🚨',
        warning: '⚠️',
        info: 'ℹ️'
      };

      const dialog = document.createElement('div');
      dialog.className = 'custom-confirm-dialog';
      dialog.style.cssText = this._dialogShellStyles();

      dialog.innerHTML = `
        <div style="
          padding: 24px;
          background: ${colors[type].bg};
          border-bottom: 1px solid ${colors[type].border};
          display: flex;
          align-items: flex-start;
          gap: 16px;
        ">
          <div style="
            font-size: 24px;
            line-height: 1;
            margin-top: 2px;
          ">${icons[type]}</div>
          <div style="flex: 1;">
            <h3 style="
              margin: 0 0 8px 0;
              font-size: 18px;
              font-weight: 600;
              color: #111827;
            ">${title}</h3>
            <div style="
              margin: 0;
              font-size: 14px;
              color: #374151;
              line-height: 1.5;
              white-space: pre-line;
            ">${message}</div>
          </div>
        </div>
        
        <div style="
          display: flex;
          gap: 12px;
          padding: 20px 24px;
          justify-content: flex-end;
        ">
          <button class="dialog-cancel" style="
            padding: 10px 20px;
            border: 1px solid #d1d5db;
            background: white;
            color: #374151;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
          ">${cancelText}</button>
          <button class="dialog-confirm" style="
            padding: 10px 20px;
            border: none;
            background: ${colors[type].btn};
            color: white;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
          ">${confirmText}</button>
        </div>
      `;

      const overlay = this._mountDialog(dialog);

      const cancelBtn = dialog.querySelector('.dialog-cancel');
      const confirmBtn = dialog.querySelector('.dialog-confirm');

      setTimeout(() => {
        if (type === 'danger') cancelBtn.focus();
        else confirmBtn.focus();
      }, 100);

      this._wireDialogButtonHover(cancelBtn, confirmBtn, signal, {
        background: colors[type].btn,
        hover: colors[type].btnHover,
      });

      const handleCancel = () => finish(false);
      const handleConfirm = () => finish(true);

      this._wireDialogDismiss({
        overlay,
        signal,
        onCancel: handleCancel,
        onConfirm: handleConfirm,
        cancelBtn,
        confirmBtn,
      });
    });
  }
}

// Crea istanza globale
window.customDialogs = new CustomDialogs();

// Funzioni di utilità globali per compatibilità
window.showToast = (message, type, duration) => window.customDialogs.showToast(message, type, duration);
window.showInput = (title, message, defaultValue, placeholder) => window.customDialogs.showInput(title, message, defaultValue, placeholder);
window.showConfirm = (title, message, confirmText, cancelText, type) => window.customDialogs.showConfirm(title, message, confirmText, cancelText, type);

export { CustomDialogs };
